import { useEffect, useState } from "react";
import { DateTime } from "luxon";

export type LinkWindow = {
  fromUtc?: string | null;
  toUtc?: string | null;
  timezone?: string | null;
};

/**
 * Whether a visibility window is open at `nowMs`.
 *
 * `fromUtc`/`toUtc` hold true UTC instants, so the comparison needs no timezone of its own —
 * the stored `timezone` exists only so the admin UI can show and edit the window as local
 * wall-clock time (see `zonedToUtcIso`). An absent window, or a window with no bounds, is
 * always open; that matches the server's `isWindowLive` in projectFairController.
 */
export const isWindowLive = (window?: LinkWindow | null, nowMs: number = Date.now()): boolean => {
  if (!window) return true;
  const from = window.fromUtc ? new Date(window.fromUtc).getTime() : null;
  const to = window.toUtc ? new Date(window.toUtc).getTime() : null;
  if (from !== null && Number.isNaN(from)) return false;
  if (to !== null && Number.isNaN(to)) return false;
  if (from !== null && nowMs < from) return false;
  if (to !== null && nowMs > to) return false;
  return true;
};

/**
 * Wall-clock text as typed by an officer ("2026-10-14T12:00") interpreted in `timezone`,
 * returned as a UTC instant. Without this an officer in one timezone scheduling a fair in
 * another silently shifts the window by the difference between them.
 */
export const zonedToUtcIso = (naive: string, timezone?: string | null): string => {
  const trimmed = (naive || "").trim();
  if (!trimmed) return "";
  const parsed = DateTime.fromISO(trimmed, { zone: timezone || "UTC" });
  if (!parsed.isValid) return "";
  return parsed.toUTC().toISO() || "";
};

/** Inverse of `zonedToUtcIso`, for binding a stored instant back into a date/time input. */
export const utcIsoToZoned = (utcIso: string | null | undefined, timezone?: string | null): string => {
  const trimmed = (utcIso || "").trim();
  if (!trimmed) return "";
  const parsed = DateTime.fromISO(trimmed, { zone: "utc" });
  if (!parsed.isValid) return "";
  return parsed.setZone(timezone || "UTC").toFormat("yyyy-LL-dd'T'HH:mm");
};

/** Just the date half of a stored instant, in `timezone` — the anchor date for a time-only window. */
export const utcIsoToZonedDate = (utcIso: string | null | undefined, timezone?: string | null): string =>
  utcIsoToZoned(utcIso, timezone).slice(0, 10);

/** Just the time half, for binding a time-only picker. */
export const utcIsoToZonedTime = (utcIso: string | null | undefined, timezone?: string | null): string =>
  utcIsoToZoned(utcIso, timezone).slice(11, 16);

/** Recombine an anchor date with a time-of-day, both in `timezone`, into a UTC instant. */
export const zonedDateAndTimeToUtcIso = (
  date: string,
  time: string,
  timezone?: string | null
): string => {
  if (!date || !time) return "";
  return zonedToUtcIso(`${date}T${time}`, timezone);
};

export const formatInZone = (utcIso: string | null | undefined, timezone?: string | null): string => {
  const trimmed = (utcIso || "").trim();
  if (!trimmed) return "";
  const parsed = DateTime.fromISO(trimmed, { zone: "utc" });
  if (!parsed.isValid) return "";
  return parsed.setZone(timezone || "UTC").toFormat("ccc, LLL d yyyy 'at' h:mm a ZZZZ");
};

/**
 * A clock that re-renders the caller on an interval so time-gated UI crosses its own
 * boundaries on its own. Attendees arrive minutes before a fair opens; without this they sit
 * on a page whose Join buttons never appear until they think to reload.
 *
 * The interval is deliberately coarse and the tick is a single state write of a number —
 * React bails out of re-rendering when the value is unchanged, so an idle page costs nothing
 * beyond the timer itself.
 */
export const useNow = (intervalMs = 30000): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    // A backgrounded tab throttles timers, so a page restored after the boundary passed
    // would still be stale. Re-read the clock the moment it becomes visible again.
    const onVisible = () => {
      if (document.visibilityState === "visible") setNow(Date.now());
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs]);
  return now;
};
